import Cookies from 'js-cookie';

const TOKEN_KEY = 'access_token';

// 登录状态变化事件监听器
type AuthStatusListener = (isLoggedIn: boolean) => void;
const authStatusListeners: Set<AuthStatusListener> = new Set();

/**
 * 获取存储的token
 */
export function getToken(): string | undefined {
  return localStorage.getItem('token') || undefined;
}

/**
 * 设置token
 */
export function setToken(token: string): void {
  const wasLoggedIn = isLoggedIn();
  Cookies.set(TOKEN_KEY, token, { expires: 7 }); // 7天过期
  localStorage.setItem('token', token);
  
  // 如果登录状态发生变化，通知所有监听器
  const nowLoggedIn = !!token;
  if (wasLoggedIn !== nowLoggedIn) {
    notifyAuthStatusChange(nowLoggedIn);
  }
}

/**
 * 清除token
 */
export function clearToken(): void {
  const wasLoggedIn = isLoggedIn();
  Cookies.remove(TOKEN_KEY);
  localStorage.removeItem('token');
  
  // 如果登录状态发生变化，通知所有监听器
  if (wasLoggedIn) {
    notifyAuthStatusChange(false);
  }
}

/**
 * 检查是否已登录
 */
export function isLoggedIn(): boolean {
  const token = getToken();
  return !!token;
}

/**
 * 解析JWT token
 */
export function parseJWT(token: string): any | null {
  try {
    // JWT格式为 header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('Invalid JWT format');
      return null;
    }
    
    // 解码payload部分（第二部分）
    const payload = parts[1];
    // 处理Base64URL编码（替换字符并添加padding）
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
    
    // 使用atob解码并解析JSON
    const decoded = atob(padded);
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Failed to parse JWT:', error);
    return null;
  }
}

/**
 * 从JWT token中获取corp_id
 */
export function getCorpIdFromToken(): string | null {
  try {
    const token = getToken();
    if (!token) {
      return null;
    }
    
    const payload = parseJWT(token);
    return payload?.corp_id || null;
  } catch (error) {
    console.error('Failed to get corp_id from token:', error);
    return null;
  }
}

/**
 * 通知所有监听器登录状态变化
 */
function notifyAuthStatusChange(isLoggedIn: boolean): void {
  authStatusListeners.forEach(listener => {
    try {
      listener(isLoggedIn);
    } catch (error) {
      console.error('Error in auth status listener:', error);
    }
  });
}

/**
 * 添加登录状态变化监听器
 */
export function addAuthStatusListener(listener: AuthStatusListener): () => void {
  authStatusListeners.add(listener);
  
  // 返回取消监听的函数
  return () => {
    authStatusListeners.delete(listener);
  };
}

/**
 * 移除登录状态变化监听器
 */
export function removeAuthStatusListener(listener: AuthStatusListener): void {
  authStatusListeners.delete(listener);
}